import { useLocation, useNavigate } from "@solidjs/router";
import { dispatchGlobalEvent } from "@storytell/events";
import { TbCoin } from "solid-icons/tb";
import { onMount, Show } from "solid-js";
import { hasPrimaryCommandCenter } from "~/components/prompt/internal/activePromptEditor";
import { useProjectBillingContext } from "~/hooks/useProjectBillingContext";
import { useI18n } from "~/i18n";
import { formatCredits } from "~/utils/creditFormat";
import { useWire } from "~/wire";

/**
 * True on the screens where this pill reports the balance. The composer's
 * percentage pill reads it to stand down, so the two never stack — this one
 * wins, since it carries the number and is there at any balance.
 */
export function showsProjectHomeCreditsPill(wire: ReturnType<typeof useWire>, pathname: string): boolean {
  const nav = wire.navigation.state();
  if (nav.primaryId || !nav.projectId || !pathname.startsWith("/project/")) return false;
  if (wire.auth?.isReady?.() && wire.auth?.isGuest?.()) return false;
  if (!wire.billing.show()) return false;
  // Until the first billing fetch lands there is no evidence the account reports a
  // balance at all, so on a cold open neither this pill nor the composer's — which
  // stands down against this same answer — claims the surface.
  return !wire.billing.loading() || wire.billing.credits() > 0;
}

/**
 * Credit balance floating over project home, which carries no top bar to hold it.
 *
 * The number is the balance of the org that pays for this project, not the viewer's
 * own org — those differ whenever a project is shared across orgs, and the paying
 * org's balance is the one a turn here debits.
 *
 * Positioned against the side panel's edge, at the same inset the panel controls use.
 */
export const ProjectHomeCreditsPill = () => {
  const wire = useWire();
  const { t, currentLanguage } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const layout = wire.ui.layout;

  const { data: billingContext } = useProjectBillingContext(() => wire.currentProjectId());
  // The shared balance otherwise refreshes only after an answer, which the homepage doesn't produce.
  onMount(() => wire.projectBilling.refetch());

  const label = (balance: number) => `${formatCredits(balance, currentLanguage())} ${t("credits_available")}`;

  // The Credit Balance view reads PromptBarContext, so the event that opens it is
  // subscribed only where a composer is mounted. This pill can paint first — the
  // page holds the composer back while project context resolves — so a click in
  // that window takes the longer route rather than landing nowhere.
  const openCreditBalance = () => {
    if (hasPrimaryCommandCenter()) {
      dispatchGlobalEvent("open-prompt-bar-usage", undefined);
      return;
    }
    const pid = wire.navigation.state().projectId;
    if (pid) navigate(`/project/${pid}/billing`);
  };

  return (
    <Show when={showsProjectHomeCreditsPill(wire, location.pathname) && billingContext()}>
      {(ctx) => (
        <div
          class="absolute top-2.75 z-20 transition-[right] duration-200"
          style={{ right: `max(0.6875rem, calc(${layout.sideSize()}px - 2.5rem))` }}
        >
          <button
            type="button"
            onClick={openCreditBalance}
            title={label(ctx().balance)}
            aria-label={label(ctx().balance)}
            class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            classList={{
              "border-primary-border bg-panel text-secondary-text hover:bg-panel-hover hover:text-primary-text":
                ctx().balance > 0,
              "border-transparent bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-950/50":
                ctx().balance <= 0,
            }}
          >
            <TbCoin class="size-3.5 shrink-0" />
            <span class="tabular-nums">{formatCredits(ctx().balance, currentLanguage())}</span>
          </button>
        </div>
      )}
    </Show>
  );
};
